import {
  Secret,
  SecretData,
  SecretDataAws,
  SecretDataAzureConnector,
  SecretDataDictionary,
  SecretDataEcr,
  SecretDataKeypair,
  SecretDataNatsAccount,
  SecretDataOpaque,
  SecretDataTls,
  SecretDataUserpass,
  SecretType,
} from "../../schema/types/secret/entity";
import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import {
  SecretDataAwsInput,
  SecretDataAzureConnectorInput,
  SecretDataEcrInput,
  SecretDataKeypairInput,
  SecretDataTlsInput,
  SecretInput,
} from "../../schema/types/secret/input";
import { SecretCreate } from "../../schema/types/secret/create";
import { v4 as uuidv4 } from "uuid";

export function getDefaultSecretInput(): SecretInput {
  return {
    ...getDefaultBaseInput(),
    id: "",
    version: 0,
    aws: {
      accessKey: "",
      externalId: "",
      roleArn: "",
      secretKey: {
        value: "",
        isVisible: false,
      },
    },
    azureConnector: {
      code: { value: "", isVisible: false },
      url: "",
    },
    azureSdk: "",
    dictionary: [],
    docker: "",
    ecr: {
      accessKey: "",
      externalId: "",
      repos: [],
      roleArn: "",
      secretKey: {
        isVisible: false,
        value: "",
      },
    },
    gcp: "",
    keypair: {
      passphrase: {
        isVisible: false,
        value: "",
      },
      publicKey: "",
      secretKey: {
        isVisible: false,
        value: "",
      },
    },
    natsAccount: {
      accountId: "",
      privateKey: {
        isVisible: false,
        value: "",
      },
    },
    opaque: {
      base64Decode: false,
      payload: "",
    },
    tls: {
      cert: "",
      chain: "",
      key: "",
    },
    userpass: {
      base64Decode: false,
      password: {
        isVisible: false,
        value: "",
      },
      username: "",
    },
    type: "opaque",
    meta: {
      isEdit: false,
      revealed: false,
      fileInputs: {},
      codeEditorVisibility: {},
    },
  };
}

export function inputToData(input: SecretInput): SecretData {
  switch (input.type) {
    case "aws":
      const aws: SecretDataAws = {
        accessKey: input.aws.accessKey,
        secretKey: input.aws.secretKey.value,
      };
      if (input.aws.roleArn) {
        aws.roleArn = input.aws.roleArn;
      }
      if (input.aws.externalId) {
        aws.externalId = input.aws.externalId;
      }
      return aws;
    case "azure-connector":
      const azureConnector: SecretDataAzureConnector = {
        url: input.azureConnector.url,
        code: input.azureConnector.code.value,
      };
      return azureConnector;
    case "azure-sdk":
      return input.azureSdk;
    case "dictionary":
      const dictionary: SecretDataDictionary = {};
      for (const item of input.dictionary) {
        dictionary[item.name] = item.value;
      }
      return dictionary;
    case "docker":
      return input.docker;
    case "ecr":
      const ecr: SecretDataEcr = {
        accessKey: input.ecr.accessKey,
        secretKey: input.ecr.secretKey.value,
        repos: input.ecr.repos.map((r) => r.value),
      };
      if (input.ecr.roleArn) {
        ecr.roleArn = input.ecr.roleArn;
      }
      if (input.ecr.externalId) {
        ecr.externalId = input.ecr.externalId;
      }
      return ecr;
    case "gcp":
      return input.gcp;
    case "keypair":
      const keypair: SecretDataKeypair = {
        secretKey: input.keypair.secretKey.value,
      };
      if (input.keypair.publicKey) {
        keypair.publicKey = input.keypair.publicKey;
      }
      if (input.keypair.passphrase.value) {
        keypair.passphrase = input.keypair.passphrase.value;
      }
      return keypair;
    case "nats-account":
      return {
        accountId: input.natsAccount.accountId,
        privateKey: input.natsAccount.privateKey.value,
      };
    case "opaque":
      return {
        encoding: input.opaque.base64Decode ? "base64" : "plain",
        payload: input.opaque.payload,
      };
    case "tls":
      const tls: SecretDataTls = {
        cert: input.tls.cert,
      };
      if (input.tls.key) {
        tls.key = input.tls.key;
      }
      if (input.tls.chain) {
        tls.chain = input.tls.chain;
      }
      return tls;
    case "userpass":
      return {
        username: input.userpass.username,
        password: input.userpass.password.value,
        encoding: input.userpass.base64Decode ? "base64" : "plain",
      };
  }
}

export function inputToSecret(input: SecretInput): SecretCreate {
  const res: SecretCreate = {
    name: input.name,
    kind: "secret",
    tags: inputToTags(input.tags),
    data: inputToData(input),
    type: input.type,
  };
  if (input.description) {
    res.description = input.description;
  }
  return res;
}

export function secretToInput(secret: Secret): SecretInput {
  const def = getDefaultSecretInput();
  const res: SecretInput = {
    ...def,
    id: secret.id,
    version: secret.version,
    name: secret.name,
    description: secret.description,
    type: secret.type,
    tags: tagsToInput(secret.tags),
    meta: {
      ...def.meta,
      isEdit: true,
    },
  };

  if (!secret.data) {
    return res;
  }

  res.meta.revealed = true;
  switch (secret.type) {
    case "aws":
      const awsData: SecretDataAws = secret.data as SecretDataAws;
      const aws: SecretDataAwsInput = {
        accessKey: awsData.accessKey,
        secretKey: {
          isVisible: false,
          value: awsData.secretKey,
        },
        externalId: awsData.externalId || "",
        roleArn: awsData.roleArn || "",
      };
      res.aws = aws;
      break;
    case "azure-connector":
      const azureConnectorData: SecretDataAzureConnector = secret.data as SecretDataAzureConnector;
      const azureConnector: SecretDataAzureConnectorInput = {
        url: azureConnectorData.url,
        code: {
          isVisible: false,
          value: azureConnectorData.code,
        },
      };
      res.azureConnector = azureConnector;
      break;
    case "azure-sdk":
      res.azureSdk = secret.data as string;
      break;
    case "dictionary":
      const dictionaryData: SecretDataDictionary = secret.data as SecretDataDictionary;
      for (const key of Object.keys(dictionaryData)) {
        res.dictionary.push({ id: uuidv4(), name: key, value: dictionaryData[key] || "", isVisible: false } as any);
      }
      break;
    case "docker":
      res.docker = secret.data as string;
      break;
    case "ecr":
      const ecrData: SecretDataEcr = secret.data as SecretDataEcr;
      const ecr: SecretDataEcrInput = {
        accessKey: ecrData.accessKey,
        secretKey: {
          isVisible: false,
          value: ecrData.secretKey,
        },
        externalId: ecrData.externalId || "",
        roleArn: ecrData.roleArn || "",
        repos: (ecrData.repos || []).map((v) => ({ id: uuidv4(), value: v })),
      };
      res.ecr = ecr;
      break;
    case "gcp":
      res.gcp = secret.data as string;
      break;
    case "keypair":
      const keypairData: SecretDataKeypair = secret.data as SecretDataKeypair;
      const keypair: SecretDataKeypairInput = {
        secretKey: {
          value: keypairData.secretKey,
          isVisible: false,
        },
        passphrase: {
          value: keypairData.passphrase || "",
          isVisible: false,
        },
        publicKey: keypairData.publicKey || "",
      };
      res.keypair = keypair;
      break;
    case "nats-account":
      res.natsAccount = {
        accountId: (secret.data as SecretDataNatsAccount).accountId,
        privateKey: {
          isVisible: false,
          value: (secret.data as SecretDataNatsAccount).privateKey,
        },
      };
      break;
    case "opaque":
      res.opaque = {
        payload: (secret.data as SecretDataOpaque).payload || "",
        base64Decode: (secret.data as SecretDataOpaque).encoding === "base64",
      };
      break;
    case "tls":
      const tlsData: SecretDataTls = secret.data as SecretDataTls;
      const tls: SecretDataTlsInput = {
        cert: tlsData.cert,
        key: tlsData.key || "",
        chain: tlsData.chain || "",
      };
      res.tls = tls;
      break;
    case "userpass":
      res.userpass = {
        username: (secret.data as SecretDataUserpass).username,
        password: {
          isVisible: false,
          value: (secret.data as SecretDataUserpass).password,
        },
        base64Decode: (secret.data as SecretDataUserpass).encoding === "base64",
      };
      break;
  }

  // Set code editor visibility flags to false for types that use CodeEditor
  const codeEditorVisibilityMap: Partial<Record<SecretType, string[]>> = {
    "azure-sdk": ["azureSdk"],
    docker: ["docker"],
    gcp: ["gcp"],
    opaque: ["opaque.payload"],
    tls: ["tls.key", "tls.cert", "tls.chain"],
    keypair: ["keypair.secretKey", "keypair.publicKey"],
  };
  const fields = codeEditorVisibilityMap[secret.type];
  if (fields) {
    const visibility: Record<string, boolean> = {};
    for (const field of fields) {
      visibility[field] = false;
    }
    res.meta.codeEditorVisibility = visibility;
  }

  return res;
}

export const SecretTypeOptions: { label: string; value: SecretType }[] = [
  { label: "AWS", value: "aws" },
  { label: "Azure Connector", value: "azure-connector" },
  { label: "Azure-SDK", value: "azure-sdk" },
  { label: "Dictionary", value: "dictionary" },
  { label: "Docker", value: "docker" },
  { label: "ECR", value: "ecr" },
  { label: "GCP", value: "gcp" },
  { label: "Keypair", value: "keypair" },
  { label: "NATS Account", value: "nats-account" },
  { label: "Opaque", value: "opaque" },
  { label: "TLS", value: "tls" },
  { label: "Username & Password", value: "userpass" },
];

export function secretToPatch(
  _secret: SecretCreate,
  options: { includeData?: boolean; concurrency: OptimisticConcurrency },
) {
  const secret: any = JSON.parse(JSON.stringify(_secret));
  const { includeData = false, concurrency } = options;

  // Only include data if explicitly requested (i.e., data was revealed/edited)
  if (includeData) {
    secret[`$replace/data`] = secret.data;
  }
  delete secret.data;

  secret[`$replace/tags`] = secret.tags;
  delete secret.tags;

  secret.id = concurrency.id;
  secret.version = concurrency.version;

  return secret;
}
